import React, { useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Button } from '@/design-system'
import { useFeatureGate, FEATURE_KEYS } from '@/context/SubscriptionContext'
import { CategoryTiles, CategoryTileOption } from '../../components/CategoryTiles'
import { FeaturedSpotlight, SpotlightPick } from '../../components/FeaturedSpotlight'
import { TrendingShelf } from '../../components/TrendingShelf'
import { PromoBand } from '../../components/PromoBand'
import { DiscoverGrid } from '../../components/DiscoverGrid'
import { DiscoverFeedItem, useDiscoverFeed } from '../../hooks/useDiscoverFeed'

type WallFilter = 'all' | 'walkable' | 'albums' | 'galleries'

const SPOTLIGHT_COUNT = 5
const TRENDING_COUNT = 8

const PageHead = styled(Box)({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: 20,
  padding: '4px 0 22px',
  flexWrap: 'wrap',
})

const Title = styled(Typography)({ fontSize: 26, fontWeight: 750, letterSpacing: '-0.032em', color: 'var(--ds-ink)' })

const Sub = styled(Typography)({
  fontSize: 13,
  color: 'var(--ds-ink-subtle)',
  marginTop: 6,
  maxWidth: 540,
  lineHeight: 1.55,
})

const Section = styled(Box)({ marginTop: 36 })

const GridHead = styled(Box)({ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16 })

const GridTitle = styled(Typography)({ fontSize: 16, fontWeight: 720, letterSpacing: '-0.02em', color: 'var(--ds-ink)' })

const GridCount = styled(Typography)({ fontFamily: 'var(--ds-font-mono)', fontSize: 10, color: 'var(--ds-ink-subtle)' })

const WallEmpty = styled(Box)({ padding: '60px 0', textAlign: 'center' })

const EmptyGlyph = styled(Box)({ fontSize: 26, opacity: 0.35, color: 'var(--ds-ink-subtle)', marginBottom: 12 })

const EmptyTitle = styled(Typography)({ fontSize: 17, fontWeight: 700, color: 'var(--ds-ink)' })

const EmptyDesc = styled(Typography)({
  fontSize: 13.5,
  color: 'var(--ds-ink-muted)',
  marginTop: 8,
  maxWidth: 360,
  marginLeft: 'auto',
  marginRight: 'auto',
  lineHeight: 1.6,
})

const Footer = styled(Box)({ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, paddingTop: 44 })

const FooterNote = styled(Typography)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9.5,
  letterSpacing: '0.14em',
  color: 'var(--ds-ink-subtle)',
  textAlign: 'center',
})

/**
 * `/discover` — other members' opt-in public albums & 3D galleries. Light,
 * colorful, browsing-first page (see docs/mockups-v19): tinted category
 * tiles drive the grid filter, an editor's-pick spotlight and a trending
 * shelf lead the page, one marketing band sits mid-feed, and every card
 * carries an explicit next action. Fed by the same `useDiscoverFeed()` the
 * previous night-theme redesign used — presentation only, no new API surface.
 */
export function DiscoverScreen() {
  const navigate = useNavigate()
  const { isFeatureAvailable, showUpgrade } = useFeatureGate()
  const [filter, setFilter] = useState<WallFilter>('all')
  const [reportedIds, setReportedIds] = useState<Set<string>>(new Set())
  const { items, albums, galleries, loading, loadingMore, hasMore, loadMore } = useDiscoverFeed()

  const visibleItems = useMemo(() => items.filter((i) => !reportedIds.has(i.id)), [items, reportedIds])
  const visibleAlbums = useMemo(() => albums.filter((a) => !reportedIds.has(a.id)), [albums, reportedIds])
  const visibleGalleries = useMemo(() => galleries.filter((g) => !reportedIds.has(g.id)), [galleries, reportedIds])

  const openItem = (item: DiscoverFeedItem) => {
    if (item.kind === 'album' && item.album) navigate(`/a/${item.album.id}`)
    else if (item.kind === 'gallery' && item.gallery) navigate(`/g/${item.gallery.id}`)
  }

  const handleReport = (item: DiscoverFeedItem) => {
    setReportedIds((prev) => new Set(prev).add(item.id))
    toast('Reported \u2014 thanks, we\u2019ll take a look.')
  }

  const goTry3D = () => {
    if (!isFeatureAvailable(FEATURE_KEYS.GALLERY_3D)) {
      showUpgrade({ featureKey: FEATURE_KEYS.GALLERY_3D })
      return
    }
    navigate('/galleries/try-3d')
  }

  const walkableCount = useMemo(
    () => visibleItems.filter((i) => i.kind === 'gallery' || Boolean(i.album?.galleryId)).length,
    [visibleItems],
  )

  const categoryOptions: CategoryTileOption[] = [
    { value: 'all', label: 'All', count: visibleItems.length, glyph: '\u2726', tintSoft: 'var(--ds-gradient-spectrum-soft)', tintInk: 'var(--ds-ink-muted)' },
    { value: 'walkable', label: 'Walkable rooms', count: walkableCount, glyph: '\u25c8', tintSoft: 'var(--ds-alert-info-soft)', tintInk: 'var(--ds-alert-info-ink)' },
    { value: 'albums', label: 'Albums', count: visibleAlbums.length, glyph: '\u25a4', tintSoft: 'var(--ds-alert-warn-soft)', tintInk: 'var(--ds-alert-warn-ink)' },
    { value: 'galleries', label: '3D Galleries', count: visibleGalleries.length, glyph: '\u25c7', tintSoft: 'var(--ds-alert-ok-soft)', tintInk: 'var(--ds-alert-ok-ink)' },
  ]

  const wallItems = useMemo(() => {
    if (filter === 'albums') return visibleItems.filter((i) => i.kind === 'album')
    if (filter === 'galleries') return visibleItems.filter((i) => i.kind === 'gallery')
    if (filter === 'walkable') return visibleItems.filter((i) => i.kind === 'gallery' || Boolean(i.album?.galleryId))
    return visibleItems
  }, [visibleItems, filter])

  const gridTitle = filter === 'all' ? 'Fresh on the wall' : categoryOptions.find((o) => o.value === filter)?.label || 'Fresh on the wall'

  // The spotlight needs a real cover photo to crossfade into, so it draws
  // from albums only — the highest-liked handful reads as "featured".
  const spotlightPicks: SpotlightPick[] = useMemo(
    () =>
      [...visibleAlbums]
        .filter((a) => a.coverUrl)
        .sort((a, b) => (b.likes || 0) - (a.likes || 0))
        .slice(0, SPOTLIGHT_COUNT)
        .map((a) => ({
          id: a.id,
          title: a.title,
          coverUrl: a.coverUrl!,
          owner: a.owner,
          photoCount: a.photoCount,
          likes: a.likes || 0,
          updatedAt: a.updatedAt,
          galleryId: a.galleryId,
          onOpen: () => navigate(`/a/${a.id}`),
          onWalk: a.galleryId ? () => navigate(`/g/${a.galleryId}`) : undefined,
        })),
    [visibleAlbums, navigate],
  )

  const trendingItems = useMemo(
    () => [...visibleItems].sort((a, b) => b.likes - a.likes).slice(0, TRENDING_COUNT),
    [visibleItems],
  )

  const isEmpty = !loading && visibleItems.length === 0
  const isFilterEmpty = !loading && !isEmpty && wallItems.length === 0

  return (
    <Box>
      <PageHead>
        <Box>
          <Title>Discover</Title>
          <Sub>
            Real albums and walkable 3D rooms other members have opened to everyone &mdash; step in and explore.
          </Sub>
        </Box>
        <Button variant='spectrum' onClick={() => navigate('/albums')}>
          &#65291; Share to Discover
        </Button>
      </PageHead>

      <CategoryTiles options={categoryOptions} value={filter} onChange={(v) => setFilter(v as WallFilter)} />

      {!loading && spotlightPicks.length > 0 && (
        <Section>
          <FeaturedSpotlight picks={spotlightPicks} />
        </Section>
      )}

      {isEmpty ? (
        <WallEmpty>
          <EmptyGlyph>&#10022;</EmptyGlyph>
          <EmptyTitle>The collection opens soon</EmptyTitle>
          <EmptyDesc>
            Once other members share an album or a walkable gallery publicly, it shows up here for everyone.
          </EmptyDesc>
        </WallEmpty>
      ) : (
        <>
          {!loading && trendingItems.length > 0 && (
            <Section>
              <TrendingShelf items={trendingItems} onSelect={openItem} />
            </Section>
          )}

          <Section>
            <PromoBand previewGallery={visibleGalleries[0]} onTry={goTry3D} onLearnMore={goTry3D} />
          </Section>

          <Section>
            <GridHead>
              <GridTitle>{gridTitle}</GridTitle>
              <GridCount>
                {wallItems.length} SHARE{wallItems.length === 1 ? '' : 'S'}
              </GridCount>
            </GridHead>
            {isFilterEmpty ? (
              <WallEmpty>
                <EmptyGlyph>&#9672;</EmptyGlyph>
                <EmptyTitle>Nothing here yet</EmptyTitle>
                <EmptyDesc>Try &ldquo;All&rdquo; &mdash; new shares land there first.</EmptyDesc>
              </WallEmpty>
            ) : (
              <DiscoverGrid items={wallItems} loading={loading} onSelectItem={openItem} onReport={handleReport} />
            )}
          </Section>

          {!loading && !isFilterEmpty && (
            <Footer>
              {hasMore ? (
                <Button variant='secondary' onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? 'Loading\u2026' : 'Load more'}
                </Button>
              ) : (
                <FooterNote>
                  THAT&rsquo;S EVERY PUBLIC SHARE &mdash; {visibleItems.length} TOTAL &middot; CHECK BACK SOON
                </FooterNote>
              )}
            </Footer>
          )}
        </>
      )}
    </Box>
  )
}
